import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { ChatBubbleOutlineIcon, PushPinIcon, SearchIcon, BookmarkIcon, AddIcon } from '@/components/ui/Icons';
import { api, type MessengerMessageSearchResult, type MessengerThread } from '@/api/client';
import {
  displayedMessengerUnreadTotalDirectPeers,
  displayedMessengerUnreadTotalGroups,
} from '@/components/Messenger/shared/sidebar/messengerSidebarUnread';
import { matchesChatContactSearchQuery } from '@/components/Messenger/shared/chatContactSearch';
import {
  readMessengerPinnedContactIds,
  writeMessengerPinnedContactIds,
} from '@/components/Messenger/shared/messengerPinnedContacts';
import {
  readMessengerPinnedGroupThreadIds,
  writeMessengerPinnedGroupThreadIds,
} from '@/components/Messenger/shared/messengerPinnedGroups';
import { messengerThreadListActivityMs } from '@/components/Messenger/shared/messengerThreadListPreview';
import { EdgeToEdgeTabs } from '@/components/ui/EdgeToEdgeTabs/EdgeToEdgeTabs';
import { ContextMenu } from '@/components/ui/ContextMenu/ContextMenu';
import { OverlayScrollArea } from '@/components/ui/OverlayScrollArea/OverlayScrollArea';
import { ChatSidebarMessageSearchResults } from '@/components/Messenger/sidebar/ChatSidebarMessageSearchResults';
import { ChatSidebarContactRow } from '@/components/Messenger/sidebar/ChatSidebarContactRow';
import { ChatSidebarGroupRow } from '@/components/Messenger/sidebar/ChatSidebarGroupRow';
import { getUserDisplayName } from '@/components/Messenger/sidebar/chatSidebarDisplay';
import { useMessengerThreadsList } from '@/hooks/useMessengerThreadsList';
import { useMessengerSidebarUnreadViewportSubtract } from '@/hooks/useMessengerSidebarUnreadViewportSubtract';
import { parseSearchHighlightWords } from '@/utils/highlightSearchTerms';
import '@/components/Messenger/styles/ChatSidebar.css';

export interface ChatContactUser {
  id: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  access?: string | null;
  role?: string | null;
}

export type ActiveContact =
  | 'agent'
  | 'saved'
  | { type: 'direct'; peerId: string; threadId: string; peerName: string }
  | { type: 'group'; threadId: string; title: string };

interface ChatSidebarProps {
  users: ChatContactUser[];
  threads: MessengerThread[];
  activeContact: ActiveContact | null;
  onSelect: (contact: ActiveContact) => void;
  currentUserId: string | null;
  /** Временно уменьшить бейдж: входящие уже в viewport (активный тред). */
  sidebarUnreadSubtractPeerId?: string | null;
  sidebarUnreadSubtractCount?: number;
  /** Для группового активного чата — вычитание непрочитанных по thread id */
  sidebarUnreadSubtractThreadId?: string | null;
  onOpenCreateGroup?: () => void;
  onSelectMessageResult?: (result: MessengerMessageSearchResult) => void;
  /** Слова активного поиска — подсветка в ленте сообщений (ChatShell → тред) */
  onSidebarSearchHighlightWordsChange?: (words: readonly string[]) => void;
  /** Меню чатов Астры — рендерится сразу под кнопкой «Астра», только при activeContact === 'agent'. */
  agentChatsMenu?: React.ReactNode;
  /** Показ блока со списком чатов под «Астрой» (сворачивание повторным кликом по «Астра»). */
  agentChatsMenuMountOpen?: boolean;
  /** Непрочитанные ответы в чатах Астры. */
  agentUnreadTotal?: number;
  /** Есть ли активный процесс в любом чате Астры. */
  agentHasProcessing?: boolean;
}

export { getUserDisplayName } from '@/components/Messenger/sidebar/chatSidebarDisplay';

export function ChatSidebar({
  users,
  threads: threadsProp,
  activeContact,
  onSelect,
  currentUserId,
  sidebarUnreadSubtractPeerId = null,
  sidebarUnreadSubtractCount = 0,
  sidebarUnreadSubtractThreadId = null,
  onOpenCreateGroup,
  onSelectMessageResult,
  onSidebarSearchHighlightWordsChange,
  agentChatsMenu,
  agentChatsMenuMountOpen = true,
  agentUnreadTotal = 0,
  agentHasProcessing = false,
}: ChatSidebarProps) {
  const threads = useMessengerThreadsList();
  const viewportSubtract = useMessengerSidebarUnreadViewportSubtract();
  void threadsProp;

  const [contactsListTab, setContactsListTab] = useState<'users' | 'groups'>('users');
  const [query, setQuery] = useState('');
  const [pinnedContactIds, setPinnedContactIds] = useState<string[]>([]);
  const [pinnedGroupIds, setPinnedGroupIds] = useState<string[]>([]);
  const [contextMenu, setContextMenu] = useState<
    | { kind: 'direct'; x: number; y: number; userId: string }
    | { kind: 'group'; x: number; y: number; threadId: string }
    | null
  >(null);
  const [messageSearchResults, setMessageSearchResults] = useState<MessengerMessageSearchResult[]>([]);
  const [messageSearchLoading, setMessageSearchLoading] = useState(false);
  const messageSearchSeqRef = useRef(0);
  const q = query.trim().toLowerCase();
  const searchActive = q.length >= 1;

  useEffect(() => {
    setPinnedContactIds(readMessengerPinnedContactIds());
    setPinnedGroupIds(readMessengerPinnedGroupThreadIds());
  }, []);

  useEffect(() => {
    if (
      activeContact !== 'agent' &&
      activeContact !== 'saved' &&
      activeContact !== null &&
      activeContact.type === 'group'
    ) {
      setContactsListTab('groups');
      return;
    }
    if (
      activeContact !== 'agent' &&
      activeContact !== 'saved' &&
      activeContact !== null &&
      activeContact.type === 'direct'
    ) {
      setContactsListTab('users');
    }
  }, [activeContact]);

  useEffect(() => {
    const queryText = query.trim();
    messageSearchSeqRef.current += 1;
    const seq = messageSearchSeqRef.current;

    if (queryText.length < 2) {
      setMessageSearchResults([]);
      setMessageSearchLoading(false);
      return;
    }

    setMessageSearchLoading(true);
    const timer = window.setTimeout(() => {
      void api.messenger
        .searchMessages(queryText, { limit: 30 })
        .then((res) => {
          if (messageSearchSeqRef.current !== seq) return;
          const seen = new Set<string>();
          const deduped = (res.results ?? []).filter((row) => {
            const id = row.message?.id;
            if (!id || seen.has(id)) return false;
            seen.add(id);
            return true;
          });
          setMessageSearchResults(deduped);
          setMessageSearchLoading(false);
        })
        .catch(() => {
          if (messageSearchSeqRef.current !== seq) return;
          setMessageSearchResults([]);
          setMessageSearchLoading(false);
        });
    }, 250);

    return () => window.clearTimeout(timer);
  }, [query]);

  const pinnedContactIdsSet = useMemo(() => new Set(pinnedContactIds), [pinnedContactIds]);
  const pinnedGroupIdsSet = useMemo(() => new Set(pinnedGroupIds), [pinnedGroupIds]);

  const handleOpenContextMenu = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, userId: string) => {
      e.preventDefault();
      setContextMenu({ kind: 'direct', x: e.clientX, y: e.clientY, userId });
    },
    []
  );

  const handleOpenGroupContextMenu = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, threadId: string) => {
      e.preventDefault();
      setContextMenu({ kind: 'group', x: e.clientX, y: e.clientY, threadId });
    },
    []
  );

  const togglePinnedContact = useCallback((userId: string) => {
    setPinnedContactIds((prev) => {
      const next = prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId];
      writeMessengerPinnedContactIds(next);
      return next;
    });
  }, []);

  const togglePinnedGroupThread = useCallback((threadId: string) => {
    setPinnedGroupIds((prev) => {
      const next = prev.includes(threadId)
        ? prev.filter((id) => id !== threadId)
        : [...prev, threadId];
      writeMessengerPinnedGroupThreadIds(next);
      return next;
    });
  }, []);

  const threadsByPeer = useMemo(() => {
    const m = new Map<string, MessengerThread>();
    for (const t of threads) {
      if (t.thread_kind === 'direct' && t.peer_id) m.set(t.peer_id, t);
    }
    return m;
  }, [threads]);

  const filteredUsers = useMemo(() => {
    if (q.length < 1) return users;
    return users.filter((u) => {
      if (matchesChatContactSearchQuery(u, q)) return true;
      const preview = threadsByPeer.get(u.id)?.last_message_preview?.trim().toLowerCase() ?? '';
      return preview.includes(q);
    });
  }, [users, q, threadsByPeer]);

  const visibleUsers = useMemo(
    () => filteredUsers.filter((u) => u.id !== currentUserId),
    [filteredUsers, currentUserId]
  );

  /** Треды, где уже есть попадание в «Сообщениях» — строка контакта/группы дублирует тот же чат с другим превью. */
  const messageSearchHitThreadIds = useMemo(
    () => new Set(messageSearchResults.map((r) => r.thread_id)),
    [messageSearchResults]
  );

  const displayVisibleUsers = useMemo(() => {
    if (!searchActive || messageSearchHitThreadIds.size === 0) return visibleUsers;
    return visibleUsers.filter((u) => {
      const tid = threadsByPeer.get(u.id)?.id;
      if (!tid) return true;
      return !messageSearchHitThreadIds.has(tid);
    });
  }, [searchActive, messageSearchHitThreadIds, visibleUsers, threadsByPeer]);

  const pinnedUsers = useMemo(() => {
    const usersById = new Map<string, ChatContactUser>();
    for (const u of displayVisibleUsers) usersById.set(u.id, u);
    const result: ChatContactUser[] = [];
    for (const id of pinnedContactIds) {
      const user = usersById.get(id);
      if (user) result.push(user);
    }
    return result;
  }, [displayVisibleUsers, pinnedContactIds]);

  /** Как в Telegram: недавние чаты выше; без переписки — внизу, по имени. */
  const unpinnedUsersSorted = useMemo(() => {
    const list = displayVisibleUsers.filter((u) => !pinnedContactIdsSet.has(u.id));
    list.sort((a, b) => {
      const ta = messengerThreadListActivityMs(threadsByPeer.get(a.id));
      const tb = messengerThreadListActivityMs(threadsByPeer.get(b.id));
      if (tb !== ta) return tb - ta;
      return getUserDisplayName(a).localeCompare(getUserDisplayName(b), 'ru');
    });
    return list;
  }, [displayVisibleUsers, pinnedContactIdsSet, threadsByPeer]);

  const groupTitle = useCallback((t: MessengerThread) => (t.title?.trim() ? t.title.trim() : 'Группа'), []);

  const groupThreads = useMemo(
    () =>
      threads
        .filter((t) => t.thread_kind === 'group')
        .sort((a, b) => messengerThreadListActivityMs(b) - messengerThreadListActivityMs(a)),
    [threads]
  );

  const filteredGroupThreads = useMemo(() => {
    if (q.length < 1) return groupThreads;
    return groupThreads.filter((t) => {
      const title = groupTitle(t).toLowerCase();
      const preview = (t.last_message_preview ?? '').trim().toLowerCase();
      const sender = (t.last_message_sender_name ?? '').trim().toLowerCase();
      return title.includes(q) || preview.includes(q) || sender.includes(q);
    });
  }, [groupThreads, q, groupTitle]);

  const pinnedGroupThreadsOrdered = useMemo(() => {
    const byId = new Map(filteredGroupThreads.map((t) => [t.id, t]));
    const out: MessengerThread[] = [];
    for (const id of pinnedGroupIds) {
      const t = byId.get(id);
      if (t) out.push(t);
    }
    return out;
  }, [filteredGroupThreads, pinnedGroupIds]);

  const unpinnedGroupThreadsSorted = useMemo(() => {
    const pinSet = pinnedGroupIdsSet;
    const list = filteredGroupThreads.filter((t) => !pinSet.has(t.id));
    list.sort((a, b) => messengerThreadListActivityMs(b) - messengerThreadListActivityMs(a));
    return list;
  }, [filteredGroupThreads, pinnedGroupIdsSet]);

  const sidebarGroupThreadsOrdered = useMemo(
    () => [...pinnedGroupThreadsOrdered, ...unpinnedGroupThreadsSorted],
    [pinnedGroupThreadsOrdered, unpinnedGroupThreadsSorted]
  );

  const displaySidebarGroupThreadsOrdered = useMemo(() => {
    if (!searchActive || messageSearchHitThreadIds.size === 0) return sidebarGroupThreadsOrdered;
    return sidebarGroupThreadsOrdered.filter((t) => !messageSearchHitThreadIds.has(t.id));
  }, [searchActive, messageSearchHitThreadIds, sidebarGroupThreadsOrdered]);

  const hasDirectUserList = pinnedUsers.length > 0 || unpinnedUsersSorted.length > 0;

  const hasGroupListRows = displaySidebarGroupThreadsOrdered.length > 0;

  const subtractPeerId = sidebarUnreadSubtractPeerId ?? viewportSubtract.peerId;
  const subtractThreadId = sidebarUnreadSubtractThreadId ?? viewportSubtract.threadId;
  const subtractCount = sidebarUnreadSubtractCount || viewportSubtract.count;

  const usersTabUnreadTotal = useMemo(
    () =>
      displayedMessengerUnreadTotalDirectPeers(threads, subtractPeerId, subtractCount),
    [threads, subtractPeerId, subtractCount]
  );

  const groupsTabUnreadTotal = useMemo(
    () =>
      displayedMessengerUnreadTotalGroups(threads, subtractThreadId, subtractCount),
    [threads, subtractThreadId, subtractCount]
  );

  const noSearchResults =
    q.length >= 1 &&
    !messageSearchLoading &&
    messageSearchResults.length === 0 &&
    filteredGroupThreads.length === 0 &&
    unpinnedUsersSorted.length === 0 &&
    pinnedUsers.length === 0;

  const searchHighlightWords = useMemo(() => parseSearchHighlightWords(query.trim()), [query]);

  useEffect(() => {
    onSidebarSearchHighlightWordsChange?.(searchHighlightWords);
  }, [searchHighlightWords, onSidebarSearchHighlightWordsChange]);

  const sidebarUserRows = useMemo(
    () => (
      <>
        {pinnedUsers.map((u) => {
          const isActive =
            activeContact !== 'agent' &&
            activeContact !== 'saved' &&
            activeContact !== null &&
            activeContact.type === 'direct' &&
            activeContact.peerId === u.id;
          return (
            <ChatSidebarContactRow
              key={`pinned-${u.id}`}
              user={u}
              isActive={isActive}
              pinned
              searchHighlightWords={searchHighlightWords}
              currentUserId={currentUserId}
              onSelect={onSelect}
              onOpenContextMenu={handleOpenContextMenu}
            />
          );
        })}
        {unpinnedUsersSorted.map((u) => {
          const isActive =
            activeContact !== 'agent' &&
            activeContact !== 'saved' &&
            activeContact !== null &&
            activeContact.type === 'direct' &&
            activeContact.peerId === u.id;
          return (
            <ChatSidebarContactRow
              key={u.id}
              user={u}
              isActive={isActive}
              pinned={false}
              searchHighlightWords={searchHighlightWords}
              currentUserId={currentUserId}
              onSelect={onSelect}
              onOpenContextMenu={handleOpenContextMenu}
            />
          );
        })}
      </>
    ),
    [
      pinnedUsers,
      unpinnedUsersSorted,
      activeContact,
      onSelect,
      handleOpenContextMenu,
      searchHighlightWords,
      currentUserId,
    ]
  );

  const sidebarGroupRows = useMemo(
    () =>
      displaySidebarGroupThreadsOrdered.map((gt) => {
        const label = groupTitle(gt);
        const isActive =
          activeContact !== 'agent' &&
          activeContact !== 'saved' &&
          activeContact !== null &&
          activeContact.type === 'group' &&
          activeContact.threadId === gt.id;
        return (
          <ChatSidebarGroupRow
            key={gt.id}
            threadId={gt.id}
            label={label}
            isActive={isActive}
            pinned={pinnedGroupIdsSet.has(gt.id)}
            searchHighlightWords={searchHighlightWords}
            currentUserId={currentUserId}
            onSelect={onSelect}
            onOpenContextMenu={handleOpenGroupContextMenu}
          />
        );
      }),
    [
      displaySidebarGroupThreadsOrdered,
      activeContact,
      onSelect,
      pinnedGroupIdsSet,
      handleOpenGroupContextMenu,
      groupTitle,
      searchHighlightWords,
      currentUserId,
    ]
  );

  return (
    <aside className="chatSidebar">
      <div className="chatSidebar__header">
        <nav className="chatSidebar__navPrimary" aria-label="Быстрый доступ">
          <button
            type="button"
            className={`chatSidebar__item ${activeContact === 'agent' ? 'chatSidebar__item_active' : ''}`}
            onClick={() => onSelect('agent')}
          >
            <ChatBubbleOutlineIcon size={20} className="chatSidebar__itemIcon" />
            <span className="chatSidebar__itemContent">
              <span className="chatSidebar__itemLabelRow">
                <span className="chatSidebar__itemLabel">Астра</span>
              </span>
              <span className="chatSidebar__itemRole chatSidebar__itemRole_caps">Агент-помощник</span>
            </span>
            {agentUnreadTotal > 0 ? (
              <span className="messengerUnreadBadge">
                {agentUnreadTotal > 99 ? '99+' : String(agentUnreadTotal)}
              </span>
            ) : null}
            {agentHasProcessing ? (
              <span className="chatSidebar__agentTabSpinner" aria-label="Астра отвечает" />
            ) : null}
          </button>

          {activeContact === 'agent' && agentChatsMenuMountOpen && agentChatsMenu ? (
            <div className="chatSidebar__agentChatsMenuMount">{agentChatsMenu}</div>
          ) : null}

          <button
            type="button"
            className={`chatSidebar__item chatSidebar__item_saved ${activeContact === 'saved' ? 'chatSidebar__item_active' : ''}`}
            onClick={() => onSelect('saved')}
          >
            <BookmarkIcon size={20} className="chatSidebar__itemIcon chatSidebar__itemIcon_saved" />
            <span className="chatSidebar__itemContent">
              <span className="chatSidebar__itemLabelRow">
                <span className="chatSidebar__itemLabel">Сохранённые сообщения</span>
              </span>
              <span className="chatSidebar__itemRole chatSidebar__itemRole_caps">Только для вас</span>
            </span>
          </button>

        </nav>

        <div className="chatSidebar__searchWrap">
          <label className="chatSidebar__searchLabel" htmlFor="chatSidebar-search">
            Поиск по чатам и сообщениям
          </label>
          <div className="chatSidebar__searchRow">
            <SearchIcon size={14} className="chatSidebar__searchIcon" aria-hidden />
            <input
              id="chatSidebar-search"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Поиск по имени и сообщениям"
              className="chatSidebar__searchInput"
              autoComplete="off"
            />
            {query ? (
              <button
                type="button"
                className="chatSidebar__searchClear"
                onClick={() => setQuery('')}
                aria-label="Очистить поиск"
              >
                ×
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {!searchActive ? (
        <div className="chatSidebar__listTabsStretch">
          <EdgeToEdgeTabs
            aria-label="Список чатов по типу"
            className="chatSidebar__listTabs"
            value={contactsListTab}
            onChange={(id) => setContactsListTab(id as 'users' | 'groups')}
            items={[
              {
                id: 'users',
                label: 'Пользователи',
                adornment:
                  usersTabUnreadTotal > 0 ? (
                    <span className="messengerUnreadBadge chatSidebar__tabUnreadBadge">
                      {usersTabUnreadTotal > 99 ? '99+' : String(usersTabUnreadTotal)}
                    </span>
                  ) : undefined,
              },
              {
                id: 'groups',
                label: 'Группы',
                adornment:
                  groupsTabUnreadTotal > 0 ? (
                    <span className="messengerUnreadBadge chatSidebar__tabUnreadBadge">
                      {groupsTabUnreadTotal > 99 ? '99+' : String(groupsTabUnreadTotal)}
                    </span>
                  ) : undefined,
              },
            ]}
          />
        </div>
      ) : null}

      <OverlayScrollArea className="chatSidebar__scroll" defer={false}>
        <div className="chatSidebar__scrollBody">
          {searchActive ? (
            <>
              <div className="chatSidebar__itemStack" role="region" aria-label="Результаты поиска">
                <ChatSidebarMessageSearchResults
                  variant="embedded"
                  results={messageSearchResults}
                  loading={messageSearchLoading}
                  query={query}
                  highlightWords={searchHighlightWords}
                  onSelect={(result) => onSelectMessageResult?.(result)}
                />
                {sidebarUserRows}
                {sidebarGroupRows}
              </div>
              {noSearchResults ? (
                <p className="chatSidebar__emptySearch" role="status">
                  Ничего не найдено
                </p>
              ) : null}
            </>
          ) : (
            <>
              <ChatSidebarMessageSearchResults
                results={messageSearchResults}
                loading={messageSearchLoading}
                query={query}
                highlightWords={searchHighlightWords}
                onSelect={(result) => onSelectMessageResult?.(result)}
              />

              {contactsListTab === 'users' ? (
                <section className="chatSidebar__section" aria-labelledby="edge-tab-users">
                  <div className="chatSidebar__itemStack">
                    {!hasDirectUserList ? (
                      <p className="chatSidebar__panelEmpty" role="status">
                        Нет пользователей в списке
                      </p>
                    ) : null}
                    {sidebarUserRows}
                  </div>
                </section>
              ) : (
                <section className="chatSidebar__section" aria-labelledby="edge-tab-groups">
                  {onOpenCreateGroup ? (
                    <div className="chatSidebar__groupAddRow">
                      <button
                        type="button"
                        className="chatSidebar__groupAddBtn chatSidebar__groupAddBtn_block"
                        onClick={() => onOpenCreateGroup()}
                      >
                        <AddIcon size={18} className="chatSidebar__groupAddBtnPlus" aria-hidden />
                        <span>Создать группу</span>
                      </button>
                    </div>
                  ) : null}
                  <div className="chatSidebar__itemStack">
                    {!hasGroupListRows ? (
                      <p className="chatSidebar__panelEmpty" role="status">
                        Нет групп
                      </p>
                    ) : null}
                    {sidebarGroupRows}
                  </div>
                </section>
              )}

              {noSearchResults ? (
                <p className="chatSidebar__emptySearch" role="status">
                  Ничего не найдено
                </p>
              ) : null}
            </>
          )}
        </div>
      </OverlayScrollArea>

      <ContextMenu
        isOpen={contextMenu !== null}
        onClose={() => setContextMenu(null)}
        anchorPosition={contextMenu ? { x: contextMenu.x, y: contextMenu.y } : null}
        items={
          contextMenu
            ? contextMenu.kind === 'direct'
              ? [
                  {
                    label: pinnedContactIdsSet.has(contextMenu.userId)
                      ? 'Открепить контакт'
                      : 'Закрепить контакт',
                    icon: <PushPinIcon size={18} />,
                    onClick: () => togglePinnedContact(contextMenu.userId),
                  },
                ]
              : [
                  {
                    label: pinnedGroupIdsSet.has(contextMenu.threadId)
                      ? 'Открепить группу'
                      : 'Закрепить группу',
                    icon: <PushPinIcon size={18} />,
                    onClick: () => togglePinnedGroupThread(contextMenu.threadId),
                  },
                ]
            : []
        }
      />
    </aside>
  );
}
